import { useEffect, useState } from 'react';
import { getEvaluationsFormateur } from '../services/formateurService';
import { formatDate, formatDateTime } from './devisUtils';
import '../assets/styles/Qcm.css';

/** 7,5 -> « 7,5 » : la virgule française, sans décimale inutile. */
const nombre = (n) =>
  n === null || n === undefined ? '—' : Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 1 });

/** « du 5 au 6 oct. 2026 » depuis les journées d'une séance. */
function periode(jours = []) {
  if (!jours.length) return '';
  return jours.length === 1
    ? formatDate(jours[0])
    : `${formatDate(jours[0])} → ${formatDate(jours[jours.length - 1])}`;
}

/**
 * Les résultats des stagiaires du formateur, formation par formation : qui a
 * été invité à l'évaluation de fin de formation, qui a répondu, la note et la
 * réussite moyennes — et le détail de chacun.
 */
function EvaluationsFormateur({ formateurId }) {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [ouvertes, setOuvertes] = useState(() => new Set());

  useEffect(() => {
    let vivant = true;
    setDonnees(null);
    setErreur(null);
    getEvaluationsFormateur(formateurId)
      .then((res) => vivant && setDonnees(res.data))
      .catch((err) => vivant && setErreur(err.message || 'Résultats indisponibles.'));
    return () => {
      vivant = false;
    };
  }, [formateurId]);

  if (erreur) return <div className="devis-state devis-state-error">{erreur}</div>;
  if (!donnees) return <div className="devis-state">Chargement…</div>;

  const { global, seances } = donnees;

  if (!seances.length) {
    return (
      <div className="devis-card clients-vide">
        Aucune évaluation envoyée pour vos formations pour l’instant : les résultats de vos stagiaires
        apparaîtront ici dès qu’ils auront répondu.
      </div>
    );
  }

  function basculer(cle) {
    setOuvertes((prev) => {
      const suite = new Set(prev);
      if (suite.has(cle)) suite.delete(cle);
      else suite.add(cle);
      return suite;
    });
  }

  return (
    <>
      {/* La note globale de ses stagiaires, toutes formations tenues. */}
      <div className="devis-card clients-demande">
        <div className="clients-section">
          <div className="clients-section-titre">Note globale de vos stagiaires</div>
          <div className="qcm-tuiles">
            <div className="qcm-tuile">
              <div className="qcm-tuile-valeur">
                {global.reussiteMoyenne === null ? '—' : `${nombre(global.reussiteMoyenne)} %`}
              </div>
              <div className="qcm-tuile-libelle">de réussite moyenne</div>
            </div>
            <div className="qcm-tuile">
              <div className="qcm-tuile-valeur">
                {global.repondus} / {global.invites}
              </div>
              <div className="qcm-tuile-libelle">stagiaires ayant répondu</div>
            </div>
            <div className="qcm-tuile">
              <div className="qcm-tuile-valeur">{global.seances}</div>
              <div className="qcm-tuile-libelle">formation{global.seances > 1 ? 's' : ''} évaluée{global.seances > 1 ? 's' : ''}</div>
            </div>
          </div>
        </div>
      </div>

      {seances.map((s) => {
        const cle = `${s.sessionId}|${s.evaluation}`;
        const ouverte = ouvertes.has(cle);
        return (
          <div className="devis-card clients-demande" key={cle}>
            <div className="clients-demande-tete">
              <div>
                <div className="clients-demande-titre">{s.formation || s.intitule}</div>
                <div className="clients-demande-sous">
                  {[s.societeNom, periode(s.jours), s.evaluation].filter(Boolean).join(' · ')}
                </div>
              </div>
              <button type="button" className="devis-btn devis-btn-sm" onClick={() => basculer(cle)}>
                {ouverte ? 'Masquer le détail' : 'Détail par stagiaire'}
              </button>
            </div>

            <div className="clients-section">
              <div className="qcm-tuiles">
                <div className="qcm-tuile">
                  <div className="qcm-tuile-valeur">
                    {s.reussiteMoyenne === null ? '—' : `${nombre(s.reussiteMoyenne)} %`}
                  </div>
                  <div className="qcm-tuile-libelle">de réussite moyenne</div>
                </div>
                <div className="qcm-tuile">
                  <div className="qcm-tuile-valeur">
                    {s.noteMoyenne === null ? '—' : `${nombre(s.noteMoyenne)} / ${s.nbQuestions}`}
                  </div>
                  <div className="qcm-tuile-libelle">note moyenne</div>
                </div>
                <div className="qcm-tuile">
                  <div className="qcm-tuile-valeur">
                    {s.repondus} / {s.invites}
                  </div>
                  <div className="qcm-tuile-libelle">ont répondu</div>
                </div>
              </div>

              {ouverte && (
                <div className="evaluations-detail">
                  {s.stagiaires.map((x) => (
                    <div className="clients-ligne" key={x.nom}>
                      <div className="clients-ligne-texte">
                        <div>{x.nom}</div>
                        <div className="clients-ligne-detail">
                          {x.repondu
                            ? `a répondu le ${formatDateTime(x.repondeLe)}`
                            : 'n’a pas encore répondu'}
                        </div>
                      </div>
                      {x.repondu && x.nbQuestions != null ? (
                        <span className="devis-status-pill devis-pill-valide">
                          {x.nbReussies} / {x.nbQuestions} · {nombre(x.reussite)} %
                        </span>
                      ) : (
                        <span className="devis-status-pill devis-pill-attente">en attente</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}

export default EvaluationsFormateur;
